import Cookies from 'js-cookie'
import {
  CONSENT_COOKIE_NAME,
  CONSENT_IDS,
  CONSENT_TYPES,
  COOKIE_DOMAINS,
  EVENT_LISTENERS,
  FULL_DETAIL,
  FUNCTIONAL_CONSENT,
  ANALYTICAL_CONSENT,
  FULL_CONSENT,
  PERSONALIZED_CONSENT,
  NECESSARY_DETAIL,
  JET_ANALYTICS_COOKIES,
  JET_FUNCTIONAL_COOKIES,
  JET_PERSONALIZED_COOKIES,
  L_JE_CONSENT_COOKIE_NAME,
  NECESSARY_CONSENT
} from '../store/modules/cookies/config'

/**
 * Names from selected cookie preferences
 * @param {Array} value from cookie preferences
 * @param {Boolean} legacySupport adding Full value
 * @returns Array of selected cookie preference types
 */
export function detailFromCookieValue(value = [], legacySupport = false) {
  const detail = []
  value.map(consent => {
    if (consent.isAccepted) {
      return detail.push(consent.consentTypeName)
    }
    return false
  })

  if (legacySupport) {
    detail.push(FULL_CONSENT)
  }
  return detail
}

/**
 * Obtain user cookie consent
 *  - New detailed Cookie banner values takes priority
 *  - Falls back on legacy cookie banner value
 *
 * @param {String} cookieName Consent Cookie name
 * @param {String} tenant that does not require consent - Australia
 * @returns Array of Strings as accepted consent statuses
 *
 * new schema does not support legacy values
 * Note: `Cookies.get` returns `String` or `undefined`
 */
export const getCookieConsentStatus = (cookieName, tenant) => {
  const status = []

  // Australia does not require consent
  if (tenant === 'au') {
    status.push(NECESSARY_CONSENT, FUNCTIONAL_CONSENT, ANALYTICAL_CONSENT, PERSONALIZED_CONSENT)
    return status
  }

  // OneWeb consent values
  const detailedConsentValues = Cookies.get(cookieName) // String or undefined
  if (detailedConsentValues) {
    const detailedConsentValuesJSON = JSON.parse(detailedConsentValues)
    if (Array.isArray(detailedConsentValuesJSON)) {
      return detailFromCookieValue(detailedConsentValuesJSON)
    }
    return status
  }

  return status
}

/**
 * Evaluate Cookie consent and return anonymous tracking value
 * @param {Array} consent Cookie consent values
 * @param {String} tenant Australia does not require consent
 * @returns Boolean value for anonymous analytics tracking
 */
export const isAnonymousTracking = (consent = [], tenant) => {
  // Australia does not require consent
  if (tenant === 'au') return false

  const anonymousTracking = !(consent.includes(ANALYTICAL_CONSENT) || consent.includes(FULL_CONSENT))
  return anonymousTracking
}

/**
 * Cookie Value based on user selection
 * @param {String} consent of consent
 */
export function cookieValueFromDetail(consent) {
  const date = new Date().toISOString()

  const value = CONSENT_TYPES.map(consentType => ({
    consentTypeId: CONSENT_IDS[consentType],
    consentTypeName: consentType,
    isAccepted: consent[consentType],
    decisionAt: date
  }))

  return value
}

/**
 * Remove non consented cookies
 * @param {Array} cookies of specified type
 * @returns Array of removed cookies
 */
export function removeCookies(cookies) {
  return cookies.map(cookie => Cookies.remove(cookie))
}

/**
 * Exec Function to remove cookies based on selection
 * @returns Function to remove cookie types
 */
function removeExtraCookies(consent) {
  CONSENT_TYPES.map(type => {
    // consent[type]<Boolean> - `true` when selected
    if (consent[type] === false) {
      switch (type) {
        case ANALYTICAL_CONSENT:
          return removeCookies(JET_ANALYTICS_COOKIES)
        case FUNCTIONAL_CONSENT:
          return removeCookies(JET_FUNCTIONAL_COOKIES)
        case PERSONALIZED_CONSENT:
          return removeCookies(JET_PERSONALIZED_COOKIES)
        default:
          return false
      }
    }
    return false
  })
}

/**
 * Sets the value for Cookie Consent from selection
 * @param {Object} value Cookie content
 * @param {Object} options Cookie attributes
 * @returns Cookie value set
 */
export function setConsentCookieValue(value, options = {}) {
  // TODO: Set an expiry
  const attr = { ...options }
  // TODO: custom encoding function, see https://github.com/jshttp/cookie#encode
  const stringValue = JSON.stringify(value)
  Cookies.set(CONSENT_COOKIE_NAME, stringValue, attr)
  return value
}

/**
 * Sets the value for L-JE Cookie Consent for compatibility
 * @param {Object} value Cookie content
 * @param {Object} options Cookie attributes
 * @returns Cookie value set
 */
export function setLjeConsentCookieValue(value, options = {}) {
  const attr = { ...options }
  Cookies.set(L_JE_CONSENT_COOKIE_NAME, value, attr)
}

/**
 * Obtains cookie value and calls a function to update
 * @param {Object} event Banner interaction
 * @param {Object} options cookie
 * @returns Cookie value
 */
function setAcceptAllConsentCookie(event, options = {}) {
  const detail = { ...FULL_DETAIL, ...event.detail }
  const value = cookieValueFromDetail(detail)
  setConsentCookieValue(value, options)
  setLjeConsentCookieValue(FULL_CONSENT, options)
  return value
}

/**
 * Obtains cookie value and calls a function to update
 * Removes non consented cookies
 * @param {Object} event Banner interaction
 * @param {Object} options cookie
 * @returns Cookie value
 */
function setCustomConsentCookie(event, options = {}) {
  const {
    necessary, functional, analytical, personalized
  } = event.detail
  const legacyConsent = (necessary && functional && analytical && personalized) ? FULL_CONSENT : NECESSARY_CONSENT
  const value = cookieValueFromDetail(event.detail)
  setConsentCookieValue(value, options)
  setLjeConsentCookieValue(legacyConsent, options)
  removeExtraCookies(event.detail)
  return value
}

/**
 * Obtains cookie value and calls a function to update
 * Removes non consented cookies
 * @param {Object} event Banner interaction
 * @param {Object} options cookie
 * @returns Cookie value
 */
function setNecessaryConsentCookie(event, options = {}) {
  const detail = { ...NECESSARY_DETAIL, ...event.detail }
  const value = cookieValueFromDetail(detail)
  setConsentCookieValue(value, options)
  setLjeConsentCookieValue(NECESSARY_CONSENT, options)
  removeExtraCookies(NECESSARY_DETAIL)
  return value
}

/**
 * Returns valid domain based on tenant and not for `localhost`
 * @param {String} tenant value
 * @returns Domain value or `null`
 */
function getCookieDomain(tenant) {
  if (!window.location.hostname.includes('localhost') && !window.location.hostname.includes('je-labs') && tenant && COOKIE_DOMAINS[tenant.toLowerCase()]) {
    return COOKIE_DOMAINS[tenant.toLowerCase()]
  }
  return null
}

/**
 * Call appropriate action based on event type
 * @param {Object} event from Cookie banner
 * @returns Event type handler (function) -> Cookie Value
 */
export function updateConsentCookie(event, tenant) {
  const {
    acceptAll, necessary, savePrefs
  } = EVENT_LISTENERS

  const options = {}
  const domain = getCookieDomain(tenant)
  if (domain) { options.domain = domain }

  switch (event.type) {
    case acceptAll:
      return setAcceptAllConsentCookie(event, options)
    case savePrefs:
      return setCustomConsentCookie(event, options)
    case necessary:
      return setNecessaryConsentCookie(event, options)
    default:
      return false
  }
}
